import type { HttpErrorResponse } from '@angular/common/http';

import type { EntityState, PickActions } from '@recruitee/ngrx';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
} from '@recruitee/ngrx';
import type { AnyOption, InputOptionsResponse, FilterPayload } from '@recruitee/views-types';

export interface InputOptionsState<Options extends AnyOption = AnyOption>
  extends InputOptionsResponse<Options> {
  id: string;
}

export interface OptionsQueryParams {
  limit: number;
  page: number;
  searchQuery?: string;
}

export interface LoadOptionsPayload {
  dataSourceId: string;
  filterId: string;
  inputId: string;
  inputOptionsId: string;
  filterValue: FilterPayload | null;
}

type State = EntityState<InputOptionsState>;
export const getFilterOptionsId = (
  dataSourceId: string,
  filterId: string,
  inputId: string,
  uuid: string,
) => `${dataSourceId}:${filterId}:${inputId}:${uuid}`;
const idSelector = (entity: { id: string }) => entity.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<InputOptionsState>()(
  'ViewsInputOptions',
  {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      const fetchOne = (
        state: State,
        payload: {
          dataSourceId: string;
          filterId: string;
          inputId: string;
          inputOptionsId: string;
          filterValue: FilterPayload | null;
          queryParams?: OptionsQueryParams;
        },
      ) => adapter.markAsPending(state, { scope: null, entities: [payload.inputOptionsId] });

      return {
        setState: (state, payload: { state: EntityState<InputOptionsState> }) => payload.state,
        fetchOne,
        fetchOneSuccess: (state, payload: { data: InputOptionsState }) =>
          pipeState(
            state,
            next => adapter.setOne(next, payload.data),
            next => adapter.markAsLoaded(next, { scope: null, entities: [payload.data.id] }),
          ),

        fetchMore: fetchOne,
        fetchMoreSuccess: (state, payload: { data: InputOptionsState }) => {
          const filterId = payload.data.id;
          const oldEntity = state.entityMap[filterId]!;
          const newEntity = {
            ...payload.data,
            options: [...oldEntity.options, ...payload.data.options],
          };
          return pipeState(
            state,
            next => adapter.setOne(next, newEntity),
            next => adapter.markAsLoaded(next, { scope: null, entities: [filterId] }),
          );
        },
        fetchMoreFailed: defaultActions.fetchOneFailed,

        validate: ngrxEvent<LoadOptionsPayload>(),
        validateSuccess: ngrxEvent<{ data: InputOptionsState }>(),
        validateFailed: ngrxEvent<{ error: HttpErrorResponse; id: string }>(),
      };
    },
  },
);

export const InputOptionsActions: PickActions<
  typeof actions,
  'fetchOne' | 'fetchMore' | 'validate'
> &
  Pick<typeof actions, 'setState'> = actions;
export const InputOptionsSelectors = {
  ...selectors,
  inputOptionsState: (state: any): EntityState<InputOptionsState> => state[namespace],
};
export { reducer as InputOptionsReducer, namespace as InputOptionsNamespace };
